/**
 * Drag-to-scroll for every horizontal carousel on the site.
 *
 * Opt in by putting `data-drag-scroll` on the scrolling element. Everything is delegated off
 * `document`, so sections re-rendered by the theme editor pick it up with no re-init.
 *
 * Two things the browser does by default get in the way and are suppressed while dragging:
 *   - dragstart on an image or link tears a ghost copy out of the page
 *   - the mouseup at the end of a drag fires a click, which would follow the link or add to cart
 */
(() => {
  const DRAG_THRESHOLD = 5; // px of travel before it counts as a drag rather than a click

  let scroller = null;
  let startX = 0;
  let startScrollLeft = 0;
  let isDragging = false;
  let suppressClick = false;

  const canScroll = (element) => element.scrollWidth > element.clientWidth + 1;

  const markDraggable = () => {
    document.querySelectorAll('[data-drag-scroll]').forEach((element) => {
      element.classList.toggle('is-draggable', canScroll(element));
    });
  };

  document.addEventListener('mousedown', (event) => {
    if (event.button !== 0) return;

    const target = event.target.closest('[data-drag-scroll]');
    if (!target || !canScroll(target)) return;

    // Leave real text fields alone — selecting inside them shouldn't drag the row
    if (event.target.closest('input, textarea, select, [contenteditable="true"]')) return;

    scroller = target;
    startX = event.clientX;
    startScrollLeft = target.scrollLeft;
    isDragging = false;
  });

  document.addEventListener('mousemove', (event) => {
    if (!scroller) return;

    const delta = event.clientX - startX;

    if (!isDragging) {
      if (Math.abs(delta) < DRAG_THRESHOLD) return;
      isDragging = true;
      scroller.classList.add('is-dragging');
    }

    scroller.scrollLeft = startScrollLeft - delta;
    event.preventDefault(); // stops the text selection that would otherwise follow the cursor
  });

  const endDrag = () => {
    if (!scroller) return;

    scroller.classList.remove('is-dragging');

    if (isDragging) {
      // The click fires synchronously after mouseup; the timeout only clears a flag left behind
      // when the pointer came up somewhere that produces no click at all.
      suppressClick = true;
      setTimeout(() => { suppressClick = false; }, 50);
    }

    scroller = null;
    isDragging = false;
  };

  document.addEventListener('mouseup', endDrag);
  document.addEventListener('mouseleave', endDrag);

  document.addEventListener('click', (event) => {
    if (!suppressClick) return;
    suppressClick = false;
    event.preventDefault();
    event.stopPropagation();
  }, { capture: true });

  document.addEventListener('dragstart', (event) => {
    if (event.target.closest('[data-drag-scroll]')) event.preventDefault();
  });

  window.addEventListener('load', markDraggable);
  window.addEventListener('resize', markDraggable);
  document.addEventListener('shopify:section:load', markDraggable);
  document.addEventListener('DOMContentLoaded', markDraggable);
  markDraggable();
})();
